import { supabase } from '../lib/supabase'
import { jobCompleteGpsPatch } from '../lib/geolocation'
import { gateJobSiteAction } from '../lib/fieldGpsGate'

export interface FieldLineItem {
  id: string
  job_id: string
  description: string
  quantity: number | null
  actual_quantity: number | null
  estimated_quantity?: number | null
  unit_type: string | null
  sort_order: number | null
  is_variable: boolean | null
  service_item_id?: string | null
  notes?: string | null
}

export interface FieldServiceItem {
  id: string
  name: string
  description?: string | null
  unit_type?: string | null
  service_category_id?: string | null
  default_checklist?: unknown
  work_description?: string | null
}

export async function fetchFieldJobLineItems(jobId: string): Promise<FieldLineItem[]> {
  const { data, error } = await supabase
    .from('job_line_items_field')
    .select('*')
    .eq('job_id', jobId)
    .order('sort_order')
  if (error) throw error
  return data || []
}

export async function fetchFieldServiceItems(): Promise<FieldServiceItem[]> {
  const { data, error } = await supabase
    .from('service_items_field')
    .select('id, name, description, unit_type, service_category_id, default_checklist, work_description')
    .order('name')
  if (error) throw error
  return data || []
}

export async function addJobLineItemFromCatalog(
  jobId: string,
  serviceItemId: string,
  quantity = 1
): Promise<FieldLineItem> {
  const { data, error } = await supabase.rpc('add_job_line_item_from_catalog', {
    p_job_id: jobId,
    p_service_item_id: serviceItemId,
    p_quantity: quantity,
  })
  if (error) throw error
  return data as FieldLineItem
}

export async function setJobLineItemActualQuantity(
  jobId: string,
  lineItemId: string,
  qty: number
): Promise<FieldLineItem> {
  const { data, error } = await supabase.rpc('set_job_line_item_actual_quantity', {
    p_job_id: jobId,
    p_line_item_id: lineItemId,
    p_qty: qty,
  })
  if (error) throw error
  return data as FieldLineItem
}

export async function completeJobField(
  jobId: string,
  opts: {
    completedUnitIds?: string[]
    carryForwardUnitIds?: string[]
    quantityUpdates?: Array<{ line_item_id: string; actual_quantity: number }>
  } = {}
): Promise<{ ok: boolean; job_id: string }> {
  const gated = await gateJobSiteAction({
    jobId,
    actionLabel: 'Job complete',
    attemptType: 'job_complete',
  })
  // Stamp complete GPS before status change so DB geofence trigger can verify
  // the same row transition (RPC alone does not accept coordinates).
  const patch = jobCompleteGpsPatch(gated.position)
  if (Object.keys(patch).length > 0) {
    const { error: gpsError } = await supabase.from('jobs').update(patch).eq('id', jobId)
    if (gpsError) throw gpsError
  }

  const { data, error } = await supabase.rpc('complete_job_field', {
    p_job_id: jobId,
    p_completed_unit_ids: opts.completedUnitIds || [],
    p_carry_forward_unit_ids: opts.carryForwardUnitIds || [],
    p_quantity_updates: opts.quantityUpdates || [],
  })
  if (error) throw error

  return data as { ok: boolean; job_id: string }
}
